import React, {FC, useEffect, useState} from 'react'
import styles from './components.module.scss'
import {ContentContainer, SubtitleSecondary, PopupContainer} from "../ShiftDetail.styles";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TimeToLeaveOutlinedIcon from '@mui/icons-material/TimeToLeaveOutlined';
import DiningOutlinedIcon from '@mui/icons-material/DiningOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import LineAxisOutlinedIcon from '@mui/icons-material/LineAxisOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import BlurLinearOutlinedIcon from '@mui/icons-material/BlurLinearOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import TagFacesIcon from '@mui/icons-material/TagFaces';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import {JobProps} from "utils/types";
import useActionApi from "../../../../hooks/useActionApi";
import Api from "../../../../api";
import FixedLoader from "components/FixedLoader/FixedLoader";

import CompleteShiftModal from "./CompleteShift";
import ResubmitShiftModal from "./ResubmitShift";
import ModalTitleContainer from "../../../../components/Modal/components/ModalTitleContainer";
import ModalShiftDescriptionContainer from "../../../../components/Modal/components/ModalShiftDescriptionContainer";
import {StyledButton} from "../../../../components/Buttons/DefaultButton/DefaultButton.styles";
import clsx from "clsx";
import {usePersistedState} from "../../../../hooks";
import {HygienistProps} from "../../../../utils/types";
import {entities} from "../../../../reduxStore";
import {getTalentParkingLabel} from "utils/parking";

export const convertDentalOfficeStatus = (status: number) => {
    if (status === 1) {
        return 'Pro'
    }
    if (status === 2) {
        return 'Elite'
    }
    return 'Active'
}

interface DataPointProps {
    icon: JSX.Element,
    label?: string,
    description: string | JSX.Element,
    isShow: boolean,
    className?: string,
}

const DataPointEndorsements:FC<DataPointProps> = ({icon, label, description, isShow, className}) => {
    return (
        isShow ? <div className={clsx(styles.dataPoint, className)}>
            <div className={styles.iconLabelEndorsement}>
                <div className={styles.icon}>{icon}</div>
                {label ? <div className={styles.label}>{label}</div> : null}
            </div>
            <b className={styles.description}>{description}</b>
        </div> : null
    )
}

const DataPoint:FC<DataPointProps> = ({icon, label, description, isShow, className}) => {
    return (
        isShow ? <div className={clsx(styles.dataPoint, className)}>
            <div className={styles.iconLabel}>
                <div className={styles.icon}>{icon}</div>
                {label ? <div className={styles.label}>{label}</div> : null}
            </div>
            <b className={styles.description}>{description}</b>
        </div> : null
    )
}

interface ShiftPopupProps {
    title: string,
    submit: string,
    close: string,
    job: JobProps,
    onClose: () => void,
    onSubmit: () => void,
    onCancel?: () => void,
    children?: any,
}

export const ShiftPopup:FC<ShiftPopupProps> = ({title, submit, close, job, onClose, onSubmit, onCancel, children}) => {
    const from = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart;
    const to = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedEnd;

    return (
        <PopupContainer>
            <ModalTitleContainer title={title} onClose={onClose} />

            <ModalShiftDescriptionContainer
                header={job.location.practiceName}
                address={job.location.address}
                from={from}
                to={to}
            />

            {children}

            <StyledButton
                variant="contained"
                color="primary"
                size="large"
                id="continue-button"
                onClick={onSubmit}
            >
                {submit}
            </StyledButton>
            <StyledButton
                variant="outlined"
                color="primary"
                size="large"
                id="continue-button"
                onClick={onCancel ? onCancel : onClose}
            >
                {close}
            </StyledButton>
        </PopupContainer>
    );
}

const AccordionExpandIcon:FC<{ job: JobProps }> = ({job}) => {
    return (
        <ContentContainer className="row" style={{margin: '1rem'}}>
            <div className={styles.dataBox}>
                <DataPoint
                    isShow={true}
                    icon={<PersonOutlineOutlinedIcon/>}
                    label={'Primary Contact'}
                    description={job.location.primaryContact}
                />
                <DataPoint
                    isShow={true}
                    icon={<TimeToLeaveOutlinedIcon/>}
                    label={'Parking'}
                    description={getTalentParkingLabel(job.location.parking)}
                />
                <DataPoint
                    isShow={true}
                    icon={<BlurLinearOutlinedIcon/>}
                    label={'Radiography'}
                    description={job.location.radiography}
                />
            </div>
            <div className={styles.dataBox}>
                <DataPoint
                    className={styles.centralBox}
                    isShow={true}
                    icon={<WavesOutlinedIcon/>}
                    label={'Ultrasonic'}
                    description={job.location.ultrasonic}
                />
                <DataPoint
                    className={styles.centralBox}
                    isShow={true}
                    icon={<TimelapseOutlinedIcon/>}
                    label={'Avg Recall'}
                    description={job.location.avgApptTime}
                />
                <DataPoint
                    className={styles.centralBox}
                    isShow={true}
                    icon={<LineAxisOutlinedIcon/>}
                    label={'Charting'}
                    description={job.location.charting}
                />
            </div>
            <div className={styles.dataBox}>
                <DataPoint
                    className={styles.centralBox}
                    isShow={true}
                    icon={<DesktopMacOutlinedIcon/>}
                    label={'Software'}
                    description={job.location.software}
                />
                <DataPoint
                    className={styles.centralBox}
                    isShow={true}
                    icon={<DiningOutlinedIcon/>}
                    label={'Lunch-Break'}
                    description={'Unpaid'}
                />
                <DataPoint
                    className={styles.centralBox}
                    isShow={!!job?.isRestorative || !!job?.isOrtho}
                    icon={<TagFacesIcon/>}
                    label={'Shift Type'}
                    description={!!job?.isRestorative ? 'Restorative' : 'Orthodontic'}
                />
                <DataPoint
                    className={styles.centralBox}
                    isShow={!!job.location?.scentFreePolicy}
                    icon={<SpaOutlinedIcon/>}
                    label={'Scent Policy'}
                    description={
                        job.location?.scentFreePolicy === 'scent-free' 
                            ? 'Scent-Free' 
                            : <span style={{ color: '#FB7672', fontWeight: 700 }}>Not Scent-Free</span>
                    }
                />
            </div>
        </ContentContainer>
    );
}


const DistanceComponent: FC<{ job: JobProps }> = ({job}) => {
    const [distanceObj, setDistanceObj] = useState(job.distanceObj);

    const [getDistanceMatrixLoading, , getDistanceMatrix] = useActionApi({
        apiMethod: Api.getDistanceMatrix,
        onSuccess: (data: any) => {
            setDistanceObj(data)
        }
    });

    const updateDistance = async () => {
        const destination = {lat: Number(job.location.lat), lng: Number(job.location.lon)};
        await getDistanceMatrix(destination)
    }

    useEffect(() => {
        updateDistance()
    }, []);

    return <>
        <SubtitleSecondary>
            {distanceObj?.distance.text} away (approx {distanceObj?.duration.text})
        </SubtitleSecondary>
        <FixedLoader
            loading={getDistanceMatrixLoading}
        />
    </>
}

const MapButton = ({job, className}: {job: JobProps, className?: string}) => {
    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];

    const hygienistAddress = hygienist?.lat + ',+' + hygienist?.lon
    const officeAddress = job.location.lat + ',+' + job.location.lon
    const url = `https://www.google.com/maps/dir/${hygienistAddress}/${officeAddress}`
    const handleClick = () => {
        // @ts-ignore
        window.Cordova ? cordova.InAppBrowser.open(url, '_system', 'location=yes')
            : window.open(url, '_blank', 'location=yes')
    }

    return <StyledButton
        className={className}
        variant="outlined"
        startIcon={<RoomOutlinedIcon/>}
        color="primary"
        size="large"
        id="continue-button"
        onClick={handleClick}>
        View Map</StyledButton>
}

export {
    DataPoint,
    DataPointEndorsements,
    AccordionExpandIcon,
    CompleteShiftModal,
    DistanceComponent,
    ResubmitShiftModal,
    MapButton,
}